/* Atlas Blog / Let Us Tell You Something — mobile-only hero correction.
   IMPORTANT: <=749px only. Desktop Canva rendering is restored/preserved.
   - Keeps the first hero at the approved wide landscape ratio.
   - Keeps all "Why..." content below intact.
   - Scales and centers the COMPLETE "LET US TELL YOU SOMETHING!" composition
     so all lines stay inside the mobile hero. */
(() => {
  const MOBILE_MAX = 749;
  const ASPECT = 2.40;
  const TITLE_SCALE = 0.72;
  const TITLE_TOP = 12;

  let applying = false;
  let observer = null;
  let resizeTimer = null;
  const originals = new Map();

  const rect = (el) => (el ? el.getBoundingClientRect() : null);

  function remember(el) {
    if (!el || originals.has(el)) return;
    originals.set(el, el.getAttribute('style'));
  }

  function restoreDesktop() {
    originals.forEach((style, el) => {
      if (!el?.isConnected) return;
      if (style == null) el.removeAttribute('style');
      else el.setAttribute('style', style);
    });
    originals.clear();
  }

  function largestMedia(hero) {
    return [...hero.querySelectorAll('video,img')]
      .filter((el) => {
        const r = rect(el);
        return r && r.width > 120 && r.height > 80;
      })
      .sort((a, b) => {
        const ar = rect(a), br = rect(b);
        return br.width * br.height - ar.width * ar.height;
      })[0] || null;
  }

  function titleText(el) {
    return (el.innerText || el.textContent || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toUpperCase();
  }

  function findFullTitleGroup(hero, heroWidth) {
    const candidates = [...hero.querySelectorAll('div')]
      .map((el) => ({ el, r: rect(el), text: titleText(el) }))
      .filter(({ r, text }) =>
        r &&
        text.includes('LET US TELL') &&
        text.includes('SOMETHING') &&
        r.width >= heroWidth * 0.72 &&
        r.width <= heroWidth * 0.90 &&
        r.height >= heroWidth * 0.40 &&
        r.height <= heroWidth * 0.60
      )
      .sort((a, b) => {
        // Prefer the outer ~350x207 composition seen in testing, not inner text wrappers.
        const aScore = Math.abs((a.r.width / heroWidth) - 0.82) + Math.abs((a.r.height / heroWidth) - 0.49);
        const bScore = Math.abs((b.r.width / heroWidth) - 0.82) + Math.abs((b.r.height / heroWidth) - 0.49);
        return aScore - bScore;
      });

    return candidates[0]?.el || null;
  }

  function applyFix() {
    if (applying) return;
    const root = document.querySelector('#root');
    if (!root) return;

    applying = true;
    if (observer) observer.disconnect();
    try {
      if (window.innerWidth > MOBILE_MAX) {
        restoreDesktop();
        return;
      }

      const hero = root.querySelector('section');
      if (!hero) return;
      const media = largestMedia(hero);
      if (!media) return;

      const heroWidth = hero.parentElement?.clientWidth || root.clientWidth || document.documentElement.clientWidth;
      const heroHeight = heroWidth / ASPECT;
      const h = `${heroHeight}px`;

      // Hero/media only. Never collapse #root, #canvify-main or #canvify-content.
      remember(hero);
      hero.style.setProperty('height', h, 'important');
      hero.style.setProperty('min-height', h, 'important');
      hero.style.setProperty('max-height', h, 'important');
      hero.style.setProperty('overflow', 'hidden', 'important');
      hero.style.setProperty('position', 'relative', 'important');

      let el = media.parentElement;
      while (el && el !== hero.parentElement) {
        if (hero.contains(el)) {
          remember(el);
          el.style.setProperty('width', '100%', 'important');
          el.style.setProperty('height', h, 'important');
          el.style.setProperty('min-height', h, 'important');
          el.style.setProperty('max-height', h, 'important');
          el.style.setProperty('top', '0', 'important');
          el.style.setProperty('left', '0', 'important');
          el.style.setProperty('transform', 'none', 'important');
          el.style.setProperty('overflow', 'hidden', 'important');
        }
        if (el === hero) break;
        el = el.parentElement;
      }

      remember(media);
      media.style.setProperty('width', '100%', 'important');
      media.style.setProperty('height', '100%', 'important');
      media.style.setProperty('position', 'absolute', 'important');
      media.style.setProperty('top', '0', 'important');
      media.style.setProperty('left', '0', 'important');
      media.style.setProperty('transform', 'none', 'important');
      media.style.setProperty('object-fit', 'cover', 'important');
      media.style.setProperty('object-position', 'center center', 'important');

      // Tested at 426px: the complete Canva title composition is ~350x207.
      // Scale the complete composition (not one line) and center it dynamically.
      const titleGroup = findFullTitleGroup(hero, heroWidth);
      if (titleGroup) {
        const tr = rect(titleGroup);
        const centeredLeft = Math.max(0, (heroWidth - tr.width * TITLE_SCALE) / 2);

        remember(titleGroup);
        titleGroup.style.setProperty('transform-origin', 'top left', 'important');
        titleGroup.style.setProperty(
          'transform',
          `translate(${centeredLeft}px, ${TITLE_TOP}px) scale(${TITLE_SCALE})`,
          'important'
        );
        titleGroup.style.setProperty('z-index', '5', 'important');
      }
    } finally {
      if (observer) observer.observe(root, { childList: true, subtree: true });
      applying = false;
    }
  }

  function repeat() {
    [0, 100, 250, 500, 900, 1500, 2500, 4000].forEach((d) =>
      setTimeout(() => requestAnimationFrame(applyFix), d)
    );
  }

  function init() {
    repeat();
    const root = document.querySelector('#root');
    if (root && !observer) {
      observer = new MutationObserver(() => {
        if (!applying) requestAnimationFrame(applyFix);
      });
      observer.observe(root, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  window.addEventListener('load', repeat, { once: true });
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(repeat, 100);
  });
})();
